import { memo, useEffect, useState, useCallback } from "react";
import AddItemButton from "js/Component/AddItemButton";
import { deleteForm, fetchAllForms, duplicateForm } from "js/Api/forms";
import toast from "react-hot-toast";
import DeleteModal from "js/Component/DeleteModal";
import DataTable from "../common/DataTable";
import FormModal from "../common/FormModal";
import moment from 'moment';

const TemplateManagement = (props) => {
    const { org, onRowClick, onFormCreated, EditTemplateItem, setEditTemplateItem } = props;

    // State management
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);

    // Modal states
    const [isFormModalVisible, setIsFormModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [editableItem, setEditableItem] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Table configuration for Templates
    const tableConfig = {
        columns: [
            { key: "formName", label: "Name", render: (item) => item?.formName || "-" },
            {
                key: "templateType",
                label: "Template Type",
                render: (item) => {
                    return item.templateType === "worker_check_in" ? "Worker Check-in" : item.templateType === "recurring" ? "Recurring" : "";
                },

            },
            {
                key: "time",
                label: "Time",
                render: (item) => {
                    return item.templateType === "worker_check_in" ? "" : item?.startTime ? moment(item?.startTime).format('hh:mm a') : "";
                },

            },
            {
                key: "recipients", label: "Recipients",
                render: (item) => {
                    return item?.sentTo === "device" ? item.recipients.toString() + " device(s)" : item?.sentTo === "policy_group" ? item.recipients.toString() + " policy group(s)" : "Everyone";
                },
            }
        ]
    };

    // Data fetching with filtering for templates (templateType !== null)
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetchAllForms(org?.id);
            if (response?.success) {
                const allData = Array.isArray(response?.data) ? response?.data : [];


                const templatesData = allData.filter(
                    (item) => {
                        const t = item?.templateType;
                        return t !== null && t !== undefined && String(t).trim() !== "";
                    }
                ).sort((a, b) => a.formName.localeCompare(b.formName));
                setItems(templatesData);

                if (EditTemplateItem != null) {
                    var item = templatesData.find(a => a.id === EditTemplateItem);
                    setEditableItem(item);
                    setIsFormModalVisible(true);
                }
            }
        } catch (error) {
            toast.error("Failed to fetch templates");
            console.error("Error fetching templates:", error);
        } finally {
            setIsLoading(false);
        }
    }, [org?.id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const OpenEditTemplate = async () => {
        await fetchData();
        setEditTemplateItem(null)
    }

    useEffect(() => {
        if (EditTemplateItem !== null) {
            OpenEditTemplate();
        }
    }, [EditTemplateItem]);

    // Event handlers
    const handleAdd = () => {
        setEditableItem(null);
        setIsFormModalVisible(true);
    };

    const handleEdit = (item) => {
        setEditableItem(item);
        setIsFormModalVisible(true);
    };

    const handleDuplicate = async (item) => {
        if (!item?.id) {
            toast.error("Invalid template ID");
            return;
        }

        setIsDuplicating(true);
        try {
            const response = await duplicateForm(item.id, false);
            if (response?.success) {
                toast.success("Template duplicated successfully");
                fetchData();
            } else {
                toast.error(response?.error || "Failed to duplicate template");
            }
        } catch (error) {
            toast.error("Failed to duplicate template");
            console.error("Error duplicating template:", error);
        } finally {
            setIsDuplicating(false);
        }
    };

    const handleDelete = (item) => {
        setItemToDelete(item);
        setIsDeleteModalVisible(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete?.id) return;

        try {
            const response = await deleteForm(itemToDelete.id);
            if (response?.success) {
                toast.success("Template deleted successfully");
                fetchData();
                setIsDeleteModalVisible(false);
                setItemToDelete(null);
            } else {
                toast.error(response?.error || "Failed to delete template");
            }
        } catch (error) {
            toast.error("Failed to delete template");
            console.error("Error deleting template:", error);
        }
    };

    const handleModalClose = () => {
        setIsFormModalVisible(false);
        setEditableItem(null);
    };

    const handleFormSaved = (savedForm, isNewForm) => {
        fetchData();
        handleModalClose();
        if (isNewForm && savedForm && onFormCreated) {
            onFormCreated(savedForm);
        }
    };

    const handleRowClick = (item) => {
        if (onRowClick) {
            onRowClick({ ...item, isTemplate: true });
        }
    };

    return (
        <>
            <AddItemButton
                buttonText="New Template"
                onClick={handleAdd}
            />
            <div className="clear"></div>
            <br />

            <DataTable
                data={items}
                columns={tableConfig.columns}
                isLoading={isLoading}
                isDuplicating={isDuplicating}
                onRowClick={handleRowClick}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                isTemplate={true}
            />

            <FormModal
                visible={isFormModalVisible}
                editableItem={editableItem}
                org={org}
                onClose={handleModalClose}
                onSaved={handleFormSaved}
                isTemplate={true}
            />

            <DeleteModal
                visible={isDeleteModalVisible}
                onClose={() => setIsDeleteModalVisible(false)}
                onCancel={() => setIsDeleteModalVisible(false)}
                onDelete={confirmDelete}
                title="Are you sure you want to delete this template?"
            />
        </>
    );
};

export default memo(TemplateManagement);